import { unifiedStartupCache } from './unifiedStartupCache';
import { campaignCache } from './campaignCacheService';
import { SubtitlePreferencesService } from './subtitlePreferencesService';
import { supabase } from '@/lib/supabase';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';
import { persistentContentCache } from './persistentContentCacheService';
import { episodesCacheService } from './episodesCacheService';
import { ContentService } from './contentService';
import { supabaseRequestDeduplicator } from './supabaseRequestDeduplicator';
import { userProfileCacheService } from './userProfileCacheService';
import { logoutSignalService } from './logoutSignalService';
import { AppStateForegroundTracker } from '@/utils/appStateForegroundTracker';

const logger = createScopedLogger('BG-REFRESH');
const campaignLogger = logger.child('CAMPAIGN');
const subtitleLogger = logger.child('SUBTITLE');
const contentLogger = logger.child('CONTENT');
const userLogger = logger.child('USER');

/**
 * Background Refresh Service
 *
 * Handles silent data refresh when app returns from background
 * Ensures cached data is fresh without impacting UI responsiveness
 */
class BackgroundRefreshService {
  private readonly REFRESH_DEBOUNCE_MS = 5000; // 5 seconds between refreshes
  private currentSmartuserId: string | null = null;
  private tracker: AppStateForegroundTracker | null = null;

  /**
   * Initialize the background refresh listener
   */
  initialize(): void {
    logger.info('Initializing background refresh service');

    if (this.tracker) return;

    this.tracker = new AppStateForegroundTracker({
      name: 'BG-REFRESH',
      debounceMs: this.REFRESH_DEBOUNCE_MS,
      onForeground: () => {
        logger.info('App returned to foreground, triggering silent refresh');
        this.performBackgroundRefresh();
      },
    });
    this.tracker.start();
  }

  /**
   * Set the current user ID for subtitle preference refresh
   */
  setCurrentUser(smartuserId: string | null): void {
    this.currentSmartuserId = smartuserId;
    logger.info(`Current user set to: ${smartuserId || 'none'}`);
  }

  /**
   * Perform silent background refresh of all cached data
   */
  private async performBackgroundRefresh(): Promise<void> {
    try {
      if (logoutSignalService.isLoggingOut) {
        logger.info('Skipping refresh (logout in progress)');
        return;
      }

      logger.info('Starting silent refresh of cached data...');

      // Get current cache state
      const cache = await unifiedStartupCache.get();
      if (!cache) {
        logger.warn('No cache found, skipping refresh');
        return;
      }

      const countryCode = cache.countryCode.toLowerCase();

      // Refresh campaign configuration (silent, non-blocking)
      this.refreshCampaignCache(countryCode).catch(err => {
        campaignLogger.error('Campaign refresh error:', err);
      });

      // Refresh subtitle preference if user is logged in (silent, non-blocking)
      if (this.currentSmartuserId) {
        this.refreshSubtitlePreference(this.currentSmartuserId).catch(err => {
          subtitleLogger.error('Subtitle preference refresh error:', err);
        });
      }

      // Refresh content cache (series, rubriques) and episodes cache (silent, non-blocking)
      this.refreshContentAndEpisodesCache(countryCode).catch(err => {
        contentLogger.error('Content and episodes cache refresh error:', err);
      });

      // Refresh user data (profile, gamification, notification preferences) - silent, non-blocking
      if (this.currentSmartuserId) {
        this.refreshUserData(this.currentSmartuserId).catch(err => {
          userLogger.error('User data refresh error:', err);
        });
      }

      logger.info('Silent refresh initiated (non-blocking)');
    } catch (error) {
      logger.error('Error during background refresh:', error);
    }
  }

  /**
   * Refresh campaign cache from database (silent, non-blocking)
   */
  private async refreshCampaignCache(countryCode: string): Promise<void> {
    try {
      campaignLogger.info(`Fetching fresh campaign configs for ${countryCode}...`);

      const [configsResult, frenchFallbackResult] = await Promise.all([
        supabase
          .from('campaign_countries_languages')
          .select('id, language_code, campaign_id, country_code, culture, free_episode_price_coins, fallback_language_code')
          .eq('country_code', countryCode),
        supabase
          .from('campaign_countries_languages')
          .select('id')
          .eq('country_code', 'fr')
          .eq('language_code', 'fr')
          .maybeSingle()
      ]);

      if (configsResult.error) {
        campaignLogger.error('Database error:', configsResult.error);
        return;
      }

      const allConfigs = configsResult.data || [];
      const frenchFallback = frenchFallbackResult.data;

      if (allConfigs.length === 0) {
        campaignLogger.warn('No configs found');
        return;
      }

      // Update in-memory cache
      campaignCache.clear();
      allConfigs.forEach(config => {
        campaignCache.set({
          id: config.id,
          campaign_id: config.campaign_id?.toString() || '',
          country_code: config.country_code || countryCode,
          language_code: config.language_code,
          culture: config.culture || null,
          free_episode_price_coins: config.free_episode_price_coins || 0,
          fallback_language_code: config.fallback_language_code || null
        });
      });
      campaignCache.markInitialized();

      const languages = allConfigs.map(config => config.language_code);

      // Update unified cache
      await unifiedStartupCache.updateCampaignData(
        allConfigs.map(config => ({
          id: config.id,
          campaign_id: config.campaign_id?.toString() || '',
          country_code: config.country_code || countryCode,
          language_code: config.language_code,
          culture: config.culture || null,
          free_episode_price_coins: config.free_episode_price_coins || 0,
          fallback_language_code: config.fallback_language_code || null,
          free_episodes_count: config.free_episodes_count ?? null
        })),
        frenchFallback?.id || null,
        languages
      );

      campaignLogger.info(`Updated ${allConfigs.length} configs in cache`);
    } catch (error) {
      campaignLogger.error('Unexpected error:', error);
    }
  }

  /**
   * Refresh subtitle preference from database (silent, non-blocking)
   */
  private async refreshSubtitlePreference(smartuserId: string): Promise<void> {
    try {
      if (logoutSignalService.isLoggingOut) return;

      subtitleLogger.info(`Fetching fresh subtitle preference for ${smartuserId}...`);

      const { data, error } = await supabase
        .from('users')
        .select('subtitle_preferred_language')
        .eq('smartuser_id', smartuserId)
        .maybeSingle();

      if (error) {
        subtitleLogger.error('Database error:', error);
        return;
      }

      const language = data?.subtitle_preferred_language || null;

      // Update unified cache
      await unifiedStartupCache.updateSubtitlePreference(smartuserId, language);

      subtitleLogger.info(`Updated subtitle preference: ${language || 'disabled'}`);
    } catch (error) {
      subtitleLogger.error('Unexpected error:', error);
    }
  }

  /**
   * Refresh content cache and episodes cache from database (silent, non-blocking)
   */
  private async refreshContentAndEpisodesCache(countryCode: string): Promise<void> {
    try {
      contentLogger.info('Starting content and episodes cache refresh...');

      // Get campaign configuration from unified cache
      const cache = await unifiedStartupCache.get();
      if (!cache || !cache.campaignConfigs || cache.campaignConfigs.length === 0) {
        contentLogger.warn('No campaign configs available, skipping');
        return;
      }

      // Use the first campaign config for the current country
      const campaignConfig = cache.campaignConfigs[0];
      const campaignId = campaignConfig.id;

      contentLogger.info(`Refreshing content for campaign: ${campaignId}`);

      // Step 1: Refresh persistent content cache (series, rubriques)
      const startTime = Date.now();

      // Fetch fresh series data
      try {
        const freshSeries = await ContentService.getSeries(campaignId, null, null, null);
        if (freshSeries && freshSeries.length > 0) {
          await persistentContentCache.setSeries(campaignId, freshSeries);
          contentLogger.info(`Refreshed ${freshSeries.length} series`);
        }
      } catch (seriesError) {
        contentLogger.error('Error refreshing series:', seriesError);
      }

      // Fetch fresh rubriques data
      try {
        const freshRubriques = await ContentService.getRubriques(campaignId, null, null, null);
        if (freshRubriques && freshRubriques.length > 0) {
          await persistentContentCache.setRubriques(campaignId, freshRubriques);
          contentLogger.info(`Refreshed ${freshRubriques.length} rubriques`);
        }
      } catch (rubriquesError) {
        contentLogger.error('Error refreshing rubriques:', rubriquesError);
      }

      const contentDuration = Date.now() - startTime;
      contentLogger.info(`Content cache refresh took ${contentDuration}ms`);

      // OPTIMIZATION: Episodes are now automatically prefetched when series are cached
      // No need for separate episode refresh step - it's handled in ContentService.getSeries()
      contentLogger.info('Episodes automatically refreshed with series (bulk prefetch)');

      const totalDuration = Date.now() - startTime;
      contentLogger.info(`Complete content refresh took ${Math.round(totalDuration / 1000)}s`);
    } catch (error) {
      contentLogger.error('Unexpected error:', error);
    }
  }

  /**
   * Refresh user data (profile, gamification, notification preferences) from database
   * This is called on background refresh and periodic refresh
   */
  private async refreshUserData(smartuserId: string): Promise<void> {
    try {
      if (logoutSignalService.isLoggingOut) return;

      userLogger.info(`Refreshing user data for ${smartuserId}...`);
      const startTime = Date.now();

      const result = await supabaseRequestDeduplicator.fetchAllUserData(smartuserId, {
        forceRefresh: true,
      });

      const duration = Date.now() - startTime;
      userLogger.info(`User data refreshed in ${duration}ms`, {
        hasProfile: !!result.profile,
        hasGamification: !!result.gamification,
        hasNotificationPrefs: !!result.notificationPreferences,
        coins: result.gamification?.totalCoins,
      });

      userProfileCacheService.setLastRefreshTimestamp();
    } catch (error) {
      userLogger.error('Unexpected error:', error);
    }
  }

  /**
   * Public method to refresh user data (can be called from other services)
   */
  async refreshUserDataPublic(smartuserId: string): Promise<void> {
    await this.refreshUserData(smartuserId);
  }

  /**
   * Manually trigger a refresh (for testing or explicit refresh requests)
   */
  async manualRefresh(): Promise<void> {
    logger.info('Manual refresh triggered');
    await this.performBackgroundRefresh();
  }

  /**
   * Cleanup listener (call on app unmount)
   */
  cleanup(): void {
    logger.info('Cleaning up background refresh service');
    if (this.tracker) {
      this.tracker.stop();
      this.tracker = null;
    }
  }
}

export const backgroundRefreshService = new BackgroundRefreshService();
